import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
// RadioGroup component will be added, using custom implementation for now
import { Textarea } from '@/components/ui/textarea'
import { useInviteMember } from '@/hooks/useTeam'
import { Loader2, Mail } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface InviteMemberModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function InviteMemberModal({ open, onOpenChange }: InviteMemberModalProps) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin' | 'member'>('member')
  const [personalMessage, setPersonalMessage] = useState('')
  const [emailError, setEmailError] = useState('')
  
  const { toast } = useToast()
  const inviteMutation = useInviteMember()

  const validateEmail = (email: string): boolean => {
    if (!email.trim()) {
      setEmailError('Email is required')
      return false
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address')
      return false
    }
    
    setEmailError('')
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateEmail(email)) {
      return
    }

    try {
      await inviteMutation.mutateAsync({
        email: email.toLowerCase().trim(),
        role,
        personal_message: personalMessage.trim() || undefined
      })

      toast({
        title: 'Invitation sent',
        description: `Successfully sent invitation to ${email}`,
      })

      // Reset form and close modal
      setEmail('')
      setRole('member')
      setPersonalMessage('')
      setEmailError('')
      onOpenChange(false)
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to send invitation'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleClose = () => {
    if (!inviteMutation.isPending) {
      setEmail('')
      setRole('member')
      setPersonalMessage('')
      setEmailError('')
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Send an invitation to join your team. They'll receive an email with a link to accept.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setEmailError('')
                  }}
                  onBlur={() => validateEmail(email)}
                  className={`pl-9 ${emailError ? 'border-destructive' : ''}`}
                  disabled={inviteMutation.isPending}
                  autoFocus
                />
              </div>
              {emailError && (
                <p className="text-sm text-destructive">{emailError}</p>
              )}
            </div>

            {/* Role Selection */}
            <div className="space-y-3">
              <Label>
                Role <span className="text-destructive">*</span>
              </Label>
              <div className="space-y-2">
                <div
                  className={`flex items-start space-x-3 rounded-md border p-4 cursor-pointer transition-colors ${
                    role === 'member' ? 'border-primary bg-primary/5' : 'hover:border-gray-300'
                  }`}
                  onClick={() => setRole('member')}
                >
                  <input
                    type="radio"
                    id="member"
                    value="member"
                    checked={role === 'member'}
                    onChange={(e) => setRole(e.target.value as 'admin' | 'member')}
                    disabled={inviteMutation.isPending}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <Label htmlFor="member" className="font-medium cursor-pointer">
                      Member (Recommended)
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Can access and use all knowledge bases and features
                    </p>
                  </div>
                </div>

                <div
                  className={`flex items-start space-x-3 rounded-md border p-4 cursor-pointer transition-colors ${
                    role === 'admin' ? 'border-primary bg-primary/5' : 'hover:border-gray-300'
                  }`}
                  onClick={() => setRole('admin')}
                >
                  <input
                    type="radio"
                    id="admin"
                    value="admin"
                    checked={role === 'admin'}
                    onChange={(e) => setRole(e.target.value as 'admin' | 'member')}
                    disabled={inviteMutation.isPending}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <Label htmlFor="admin" className="font-medium cursor-pointer">
                      Admin
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Can invite members, manage team, and access all features
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Personal Message */}
            <div className="space-y-2">
              <Label htmlFor="message">
                Personal Message <span className="text-muted-foreground text-xs">(Optional)</span>
              </Label>
              <Textarea
                id="message"
                placeholder="Add a personal note to the invitation email..."
                value={personalMessage}
                onChange={(e) => setPersonalMessage(e.target.value)}
                maxLength={250}
                rows={3}
                disabled={inviteMutation.isPending}
              />
              <p className="text-xs text-muted-foreground text-right">
                {personalMessage.length}/250 characters
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={inviteMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={inviteMutation.isPending}>
              {inviteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Invitation'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
